<template>
  <div class="chatbot-container">
    <!-- Floating toggle button — ẩn khi đang expanded (window chiếm cạnh phải) -->
    <button v-show="!isExpanded" class="chatbot-toggle-btn" @click="toggleChat" :title="isOpen ? 'Đóng' : 'Trợ lý AI'">
      <i :class="isOpen ? 'fa fa-times' : 'fa fa-comments'" />
    </button>

    <!-- Chat window (v-show keeps DOM + history alive when toggled) -->
    <div v-show="isOpen" :class="['chatbot-window shadow', { 'chatbot-window--expanded': isExpanded }]">
      <div class="chatbot-header">
        <i class="fa fa-robot mr-2" />
        <span class="bold text-white">Trợ lý GISERP AI</span>
        <button class="chatbot-close-btn ml-auto" title="Xoá lịch sử chat" @click="clearHistory">
          <i class="fa fa-trash" />
        </button>
        <button class="chatbot-close-btn ml-2" :title="isExpanded ? 'Thu nhỏ' : 'Mở rộng'" @click="isExpanded = !isExpanded">
          <i :class="isExpanded ? 'fa fa-compress' : 'fa fa-expand'" />
        </button>
        <button class="chatbot-close-btn ml-2" @click="isOpen = false">
          <i class="fa fa-times" />
        </button>
      </div>

      <div class="chatbot-messages" ref="messagesContainer">
        <!-- Welcome message when no conversation yet -->
        <div v-if="displayMessages.length === 0" class="message bot-message">
          <p>Xin chào! Tôi có thể giúp bạn:</p>
          <ul>
            <li>Tìm kiếm sản phẩm, khách hàng</li>
            <li>Tạo sản phẩm, khách hàng mới</li>
            <li>Cập nhật thông tin sản phẩm, khách hàng</li>
          </ul>
          <p>Bạn cần gì?</p>
        </div>

        <div
          v-for="(msg, idx) in displayMessages"
          :key="idx"
          :class="['message', msg.role === 'user' ? 'user-message' : 'bot-message']"
        >
          <!-- Tool call indicator -->
          <div v-if="msg.toolCall" class="tool-indicator">
            <i class="fa fa-cog fa-spin mr-1" />
            <span>{{ getToolLabel(msg.toolCall) }}</span>
          </div>
          <!-- Regular text message -->
          <div v-else-if="msg.role === 'assistant'" class="md" v-html="formatText(msg.text)" />
          <p v-else class="msg-text" v-html="formatText(msg.text)" />
        </div>

        <!-- Streaming message đang gõ dần -->
        <div v-if="streamingText" class="message bot-message">
          <div class="md" v-html="formatText(streamingText)" /><span class="cursor">▋</span>
        </div>

        <!-- Loading dots — chỉ hiện khi chờ response, chưa stream -->
        <div v-if="isLoading && !streamingText" class="message bot-message loading-dots">
          <span /><span /><span />
        </div>
      </div>

      <div class="chatbot-input-area">
        <input
          ref="inputField"
          v-model="inputText"
          type="text"
          class="form-control"
          placeholder="Nhập tin nhắn..."
          :disabled="isLoading"
          @keyup.enter="sendMessage"
        />
        <button
          class="btn btn-primary ml-2 chatbot-send-btn"
          :disabled="isLoading || !inputText.trim()"
          @click="sendMessage"
        >
          <i class="fa fa-paper-plane" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { sendChat } from '@/services/chatbot'
import { marked } from 'marked'

marked.setOptions({ breaks: true, gfm: true })

const STORAGE_KEY = 'chatbot_history'
const DRAFT_KEY = 'chatbot_draft'

export default {
  name: 'ChatBot',
  data () {
    return {
      isOpen: false,
      isExpanded: false,
      inputText: '',
      isLoading: false,
      streamingText: '',
      displayMessages: [],
      anthropicMessages: [],
    }
  },
  created () {
    this.loadHistory()
  },
  watch: {
    inputText (val) {
      localStorage.setItem(DRAFT_KEY, val)
    },
    displayMessages: {
      deep: true,
      handler (val) {
        // Chỉ lưu các tin nhắn text (bỏ tool call indicator)
        const toSave = val.filter((m) => m.text)
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            display: toSave,
            api: this.anthropicMessages,
          }))
        } catch (e) {
          // localStorage đầy — bỏ qua
        }
      },
    },
  },
  methods: {
    loadHistory () {
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const { display, api } = JSON.parse(raw)
        if (Array.isArray(display)) this.displayMessages = display
        if (Array.isArray(api)) this.anthropicMessages = api
        this.inputText = localStorage.getItem(DRAFT_KEY) || ''
      } catch (e) {
        // dữ liệu hỏng — bỏ qua
      }
    },

    clearHistory () {
      this.displayMessages = []
      this.anthropicMessages = []
      localStorage.removeItem(STORAGE_KEY)
    },

    toggleChat () {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.$nextTick(() => {
          if (this.$refs.inputField) this.$refs.inputField.focus()
          this.scrollToBottom()
        })
      }
    },

    async sendMessage () {
      const text = this.inputText.trim()
      if (!text || this.isLoading) return

      this.displayMessages.push({ role: 'user', text })
      this.anthropicMessages.push({ role: 'user', content: text })
      this.inputText = ''
      localStorage.removeItem(DRAFT_KEY)
      this.isLoading = true
      this.scrollToBottom()

      try {
        const { messages, reply } = await sendChat(
          this.anthropicMessages,
          (toolName) => {
            this.displayMessages.push({ role: 'assistant', toolCall: toolName })
            this.scrollToBottom()
          },
          (chunk) => {
            // Mỗi từ stream về, append vào streamingText
            this.streamingText += chunk
            this.scrollToBottom()
          }
        )

        this.anthropicMessages = messages
        // Chuyển streamingText thành message hoàn chỉnh
        this.streamingText = ''
        this.displayMessages.push({ role: 'assistant', text: reply })
      } catch (err) {
        this.streamingText = ''
        this.displayMessages.push({
          role: 'assistant',
          text: 'Đã xảy ra lỗi, vui lòng thử lại. (' + (err.message || 'Lỗi không xác định') + ')',
        })
      } finally {
        this.isLoading = false
        this.scrollToBottom()
      }
    },

    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    getToolLabel (toolName) {
      const labels = {
        search_products: 'Đang tìm sản phẩm...',
        create_product: 'Đang tạo sản phẩm...',
        update_product: 'Đang cập nhật sản phẩm...',
        search_customers: 'Đang tìm khách hàng...',
        create_customer: 'Đang tạo khách hàng...',
        update_customer: 'Đang cập nhật khách hàng...',
        search_orders: 'Đang tìm đơn hàng...',
        get_order_detail: 'Đang lấy chi tiết đơn hàng...',
        get_order_process: 'Đang xem tiến trình đơn hàng...',
        create_order: 'Đang tạo đơn hàng...',
        search_quotes: 'Đang tìm báo giá...',
        create_quote: 'Đang tạo báo giá...',
        search_leads: 'Đang tìm leads...',
        create_leads: 'Đang tạo leads...',
        update_lead: 'Đang cập nhật lead...',
        convert_lead_to_customer: 'Đang chuyển lead thành khách hàng...',
        search_contracts: 'Đang tìm hợp đồng...',
        create_contract: 'Đang tạo hợp đồng...',
        update_contract: 'Đang cập nhật hợp đồng...',
        search_bookings: 'Đang tìm booking...',
        create_booking: 'Đang tạo booking...',
        update_booking: 'Đang cập nhật booking...',
        search_packs: 'Đang tìm phiếu đóng gói...',
        create_pack: 'Đang tạo phiếu đóng gói...',
        update_pack: 'Đang cập nhật phiếu đóng gói...',
        search_routes: 'Đang tìm tuyến...',
        create_route: 'Đang tạo tuyến...',
        update_route: 'Đang cập nhật tuyến...',
      }
      return labels[toolName] || 'Đang xử lý...'
    },

    formatText (text) {
      if (!text) return ''
      return marked.parse(text)
    },
  },
}
</script>

<style   lang="scss">
.chatbot-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
}

.chatbot-toggle-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: $main-color;
  color: #fff;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.08);
  }
}

.chatbot-window {
  position: absolute;
  bottom: 64px;
  right: 0;
  width: 360px;
  height: 500px;
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  transition: width 0.25s ease, height 0.25s ease;

  &--expanded {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 33.333vw;
    height: 100vh;
    border-radius: 12px 0 0 12px;
    border-right: none;
    z-index: 9999;
  }
}

.chatbot-header {
  background: $main-color;
  color: #fff;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  font-size: 14px;
  flex-shrink: 0;
}

.chatbot-close-btn {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
}

.chatbot-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  ul {
    margin: 4px 0 4px 18px;
    padding: 0;
    font-size: 13px;
  }
}

.message {
  max-width: 82%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.6;

  p.msg-text {
    margin: 0;
    word-break: break-word;
  }

  // Markdown styles (bot message)
  .md {
    word-break: break-word;

    p { margin: 0 0 6px; &:last-child { margin-bottom: 0; } }

    ul, ol {
      margin: 4px 0 6px 18px;
      padding: 0;
      li { margin-bottom: 2px; }
    }

    h1, h2, h3 {
      font-size: 16px;
      font-weight: 700;
      margin: 8px 0 4px;
    }

    code {
      background: rgba(0,0,0,0.08);
      border-radius: 4px;
      padding: 1px 5px;
      font-size: 12px;
      font-family: monospace;
    }

    pre {
      background: #1e1e2e;
      color: #cdd6f4;
      border-radius: 8px;
      padding: 10px 12px;
      overflow-x: auto;
      margin: 6px 0;
      code {
        background: none;
        padding: 0;
        color: inherit;
        font-size: 12px;
      }
    }

    blockquote {
      border-left: 3px solid #ccc;
      margin: 4px 0;
      padding-left: 10px;
      color: #666;
      font-style: italic;
    }

    a { color: #1a73e8; text-decoration: underline; }

    table {
      border-collapse: collapse;
      width: 100%;
      margin: 6px 0;
      font-size: 12px;
      th, td {
        border: 1px solid #ddd;
        padding: 4px 8px;
        text-align: left;
      }
      th { background: rgba(0,0,0,0.06); font-weight: 600; }
    }

    hr { border: none; border-top: 1px solid #ddd; margin: 8px 0; }

    strong { font-weight: 700; }
    em { font-style: italic; }
  }

  .chatbot-window--expanded & {
    max-width: 72%;
    font-size: 14px;
  }
}

.user-message {
  align-self: flex-end;
  background: $main-color;
  color: #fff;
  border-bottom-right-radius: 4px;
}

.bot-message {
  align-self: flex-start;
  background: #f2f3f5;
  color: #2e2e2e;
  border-bottom-left-radius: 4px;
}

.tool-indicator {
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;
}

// Loading dots animation
.loading-dots {
  padding: 10px 14px;

  span {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #aaa;
    margin: 0 2px;
    animation: chatbot-bounce 1.2s infinite ease-in-out;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

.cursor {
  display: inline-block;
  animation: blink 0.7s step-end infinite;
  font-size: 12px;
  line-height: 1;
  vertical-align: middle;
  margin-left: 1px;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
}

@keyframes chatbot-bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40%           { transform: scale(1);   opacity: 1; }
}

.chatbot-input-area {
  display: flex;
  padding: 10px 12px;
  border-top: 1px solid #eee;
  flex-shrink: 0;

  .form-control {
    font-size: 13px;
    border-radius: 20px;
    padding: 6px 14px;
  }
}

.chatbot-send-btn {
  border-radius: 50%;
  width: 36px;
  height: 36px;
  padding: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
